// NeoCore · BI Créditos · Momentum (v2, 27-oct-2026)
// -----------------------------------------------------------------------------
// Rehecho con la forma en que Hugo evalúa la cartera (migración 0392/0393):
// días CALENDARIO desde la cuota vencida más antigua · al día (0) · atraso (1–14)
// · pre-mora (15–30) · mora (31+). Toda la cartera (emergente + consumo), sin
// créditos de prueba. Lidera con el crecimiento, pero la mora va separada y a la vista.
// Fuente: RPC bi_momentum_riesgo().
import { useCallback, useEffect, useState } from 'react'
import { ArrowClockwise, Rocket, Lifebuoy } from '@phosphor-icons/react'
import {
  ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip, CartesianGrid,
  ComposedChart, Bar, Line, Legend,
} from 'recharts'
import { supabase } from '@/lib/supabase'
import { formatQ } from '@/lib/format'
import { useIsMobile } from '@/hooks/use-mobile'

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type AnyObj = any
type Cat = 'al_dia' | 'atraso' | 'pre_mora' | 'mora'

const C = {
  brand: '#FF5B04', brandSoft: '#fff1e6', cream: '#fbf7f3',
  ink: '#14171c', ink2: '#52525b', muted: '#9aa0a6', line: '#efece7',
}
// Mismos colores que RIESGO_META (components/RiesgoCartera) para que todo NeoCore hable igual
const CAT: Record<Cat, { l: string; sub: string; c: string }> = {
  al_dia: { l: 'Al día', sub: '0 días', c: '#15803D' },
  atraso: { l: 'Atraso', sub: '1–14 días', c: '#CA8A04' },
  pre_mora: { l: 'Pre-mora', sub: '15–30 días', c: '#EA580C' },
  mora: { l: 'Mora', sub: '31+ días', c: '#DC2626' },
}
const CATS: Cat[] = ['al_dia', 'atraso', 'pre_mora', 'mora']
const MESES = ['', 'Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic']
const mesL = (m: string) => `${MESES[Number(m.slice(5, 7))]} ${m.slice(2, 4)}`
const PASO = ['', '1er crédito', '2do', '3ro', '4to+']
const DISPLAY = "'DM Sans','Noto Sans',system-ui,sans-serif"
const card: React.CSSProperties = { background: '#fff', border: `1px solid ${C.line}`, borderRadius: 20, boxShadow: '0 1px 2px rgba(20,23,28,.03), 0 12px 30px -14px rgba(20,23,28,.14)' }
const cap: React.CSSProperties = { fontSize: 10.5, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.09em', color: C.muted }
const h2: React.CSSProperties = { fontFamily: DISPLAY, fontSize: 16, fontWeight: 800, color: C.ink }
const pct = (n: number | null | undefined, d = 1) => `${Number(n ?? 0).toFixed(d)}%`

function Tip({ active, payload, label, fmt }: AnyObj) {
  if (!active || !payload?.length) return null
  return (
    <div style={{ ...card, borderRadius: 12, padding: '8px 11px', fontSize: 12 }}>
      {label != null && <div style={{ fontWeight: 700, color: C.ink, marginBottom: 3 }}>{label}</div>}
      {payload.map((p: AnyObj, i: number) => (
        <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <span style={{ width: 8, height: 8, borderRadius: 99, background: p.color || p.fill }} />
          <span style={{ color: C.muted }}>{p.name}:</span><b style={{ color: C.ink }}>{fmt ? fmt(p.value, p.dataKey) : p.value}</b>
        </div>
      ))}
    </div>
  )
}

export function NeoCoreBIMomentumPage() {
  const mobile = useIsMobile()
  const [d, setD] = useState<AnyObj>(null)
  const [loading, setLoading] = useState(true)
  const [err, setErr] = useState<string | null>(null)

  const load = useCallback(() => {
    setLoading(true); setErr(null)
    supabase.rpc('bi_momentum_riesgo').then(({ data, error }) => {
      if (error) setErr(error.message); else setD(data)
      setLoading(false)
    })
  }, [])
  useEffect(() => { load() }, [load])

  if (loading) return <div style={{ padding: 64, textAlign: 'center', color: C.muted, fontFamily: DISPLAY }}>Cargando momentum…</div>
  if (err) return <div style={{ padding: 16 }}><div style={{ ...card, borderColor: '#f3c8bf', background: '#fdf1ee', color: '#a3402d', padding: 16 }}>{err}</div></div>
  if (!d) return null

  const cart = d.cartera ?? {}, tot = d.totales ?? {}, rec = d.recuperacion ?? {}, pun = d.puntualidad ?? {}, op = d.oportunidad ?? {}
  const r = (c: Cat) => (d.riesgo ?? []).find((x: AnyObj) => x.cat === c) ?? { n: 0, saldo: 0, pct: 0 }
  const al = r('al_dia'), at = r('atraso'), pm = r('pre_mora'), mo = r('mora')
  const mensual: AnyObj[] = (d.mensual ?? []).map((m: AnyObj) => ({ ...m, l: mesL(m.mes) }))
  const mAct = mensual[mensual.length - 1], mAnt = mensual[mensual.length - 2]
  const crecInt = mAct && mAnt && mAnt.interes > 0 ? Math.round((mAct.interes / mAnt.interes - 1) * 100) : null
  // Tendencia: el último punto se alinea al corte oficial (cartera_riesgo) para que cuadre con el hero
  const tend: AnyObj[] = (d.tendencia ?? []).map((t: AnyObj, i: number, arr: AnyObj[]) => {
    const ult = i === arr.length - 1
    return {
      l: `${Number(t.d.slice(8, 10))} ${MESES[Number(t.d.slice(5, 7))]}`,
      al_dia: ult ? al.pct : t.al_dia ?? 0, atraso: ult ? at.pct : t.atraso ?? 0,
      pre_mora: ult ? pm.pct : t.pre_mora ?? 0, mora: ult ? mo.pct : t.mora ?? 0,
    }
  })
  const moraSem = tend.length >= 2 ? mo.pct - tend[tend.length - 2].mora : 0
  const escalera: AnyObj[] = d.escalera ?? []
  const cohortes: AnyObj[] = d.cohortes ?? []
  const pTot = pun.total || 1
  const grid2: React.CSSProperties = { display: 'grid', gridTemplateColumns: mobile ? '1fr' : '1.4fr 1fr', gap: 12, marginTop: 14 }

  return (
    <div style={{ background: C.cream, margin: '-16px', padding: mobile ? '16px 12px 40px' : '20px 24px 48px', minHeight: '100%', fontFamily: "'Noto Sans',system-ui,sans-serif" }}>
      <div style={{ maxWidth: 1080, margin: '0 auto', animation: 'ncfade .45s ease' }}>
        <style>{`@keyframes ncfade{from{opacity:0;transform:translateY(8px)}to{opacity:1;transform:none}}`}</style>

        {/* HEADER */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16, gap: 10 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <img src="/brand/isotipo-naranja.svg" alt="" style={{ height: 30 }} onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = 'none' }} />
            <div>
              <div style={{ fontFamily: DISPLAY, fontSize: 20, fontWeight: 800, color: C.ink, letterSpacing: '-.02em' }}>Créditos · Momentum</div>
              <div style={{ fontSize: 12, color: C.muted }}>Toda la cartera · días calendario · sin créditos de prueba</div>
            </div>
          </div>
          <button onClick={load} style={{ display: 'inline-flex', alignItems: 'center', gap: 7, borderRadius: 999, border: `1px solid ${C.brand}`, background: '#fff', color: C.brand, padding: '8px 15px', fontSize: 12.5, fontWeight: 700, cursor: 'pointer' }}><ArrowClockwise size={15} weight="bold" />{!mobile && ' Actualizar'}</button>
        </div>

        {/* 1 · HERO: crecimiento + salud en una sola lectura */}
        <div style={{ ...card, padding: mobile ? 18 : 26, background: `linear-gradient(115deg, ${C.brandSoft}, #fff 55%)` }}>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 24, justifyContent: 'space-between' }}>
            <div style={{ flex: '1 1 260px' }}>
              <div style={{ ...cap, color: C.brand }}>Cartera viva</div>
              <div style={{ fontFamily: DISPLAY, fontSize: mobile ? 38 : 46, fontWeight: 800, color: C.ink, letterSpacing: '-.03em', lineHeight: 1.05, marginTop: 4 }}>{formatQ(cart.saldo ?? 0)}</div>
              <div style={{ fontSize: 12.5, color: C.ink2, marginTop: 6 }}>
                {cart.creditos} créditos · emergente {formatQ(cart.emergente ?? 0)} · consumo {formatQ(cart.consumo ?? 0)}
              </div>
            </div>
            <div style={{ flex: '1 1 220px' }}>
              <div style={{ ...cap, color: C.brand }}>Interés cobrado · {mAct ? mesL(mAct.mes) : '—'}</div>
              <div style={{ fontFamily: DISPLAY, fontSize: mobile ? 30 : 34, fontWeight: 800, color: C.ink, letterSpacing: '-.02em', marginTop: 4 }}>{formatQ(mAct?.interes ?? 0)}</div>
              <div style={{ fontSize: 12.5, color: C.ink2, marginTop: 4 }}>
                {crecInt != null && <b style={{ color: CAT.al_dia.c }}>{crecInt >= 0 ? '+' : ''}{crecInt}% </b>}
                vs {mAnt ? mesL(mAnt.mes) : '—'} ({formatQ(mAnt?.interes ?? 0)}) · total {formatQ(tot.interes ?? 0)}
              </div>
            </div>
          </div>

          {/* Las 3 cifras con las que evaluás */}
          <div style={{ display: 'grid', gridTemplateColumns: mobile ? '1fr 1fr 1fr' : 'repeat(3,1fr)', gap: mobile ? 8 : 14, marginTop: 22 }}>
            <Big v={pct(al.pct)} l="Al día" sub={`${al.n} · ${formatQ(al.saldo)}`} c={CAT.al_dia.c} mobile={mobile} />
            <Big v={pct(at.pct + pm.pct)} l="En gestión 1–30 d" sub={`${at.n + pm.n} · ${formatQ(at.saldo + pm.saldo)}`} c={CAT.pre_mora.c} mobile={mobile} />
            <Big v={pct(mo.pct)} l="Mora 31+ d" sub={`${mo.n} · ${formatQ(mo.saldo)}`} c={CAT.mora.c} mobile={mobile} />
          </div>
          <div style={{ display: 'flex', height: 12, borderRadius: 999, overflow: 'hidden', margin: '16px 0 10px', background: '#f1efe8' }}>
            {CATS.map((c) => r(c).pct > 0 && <div key={c} title={`${CAT[c].l} ${pct(r(c).pct)}`} style={{ width: `${r(c).pct}%`, background: CAT[c].c }} />)}
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px 16px', fontSize: 11.5, color: C.ink2 }}>
            {CATS.map((c) => (
              <span key={c} style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                <span style={{ width: 9, height: 9, borderRadius: 3, background: CAT[c].c }} />{CAT[c].l} <span style={{ color: C.muted }}>{CAT[c].sub}</span> <b>{pct(r(c).pct)}</b>
              </span>
            ))}
          </div>
        </div>

        {/* 2 · STATS acumulados */}
        <div style={{ display: 'grid', gridTemplateColumns: mobile ? '1fr 1fr' : 'repeat(4,1fr)', gap: 12, marginTop: 14 }}>
          <Stat label="Colocado" value={formatQ(tot.colocado ?? 0)} sub={`${tot.creditos_otorgados} créditos`} />
          <Stat label="Recaudado" value={formatQ(tot.recaudado ?? 0)} sub={`${tot.creditos_pagados} créditos pagados`} accent={CAT.al_dia.c} />
          <Stat label="Recompra" value={`${tot.asociados ? Math.round((100 * tot.recompra) / tot.asociados) : 0}%`} sub={`${tot.recompra} de ${tot.asociados} volvieron`} accent={C.brand} />
          <Stat label="Paga a tiempo" value={`${Math.round((100 * ((pun.anticipado ?? 0) + (pun.el_dia ?? 0))) / pTot)}%`} sub={`${Math.round((100 * (pun.anticipado ?? 0)) / pTot)}% anticipado · cuotas pagadas`} accent={CAT.al_dia.c} />
        </div>

        {/* 3 · MOMENTUM mensual + TENDENCIA del riesgo */}
        <div style={grid2}>
          <div style={{ ...card, padding: 20 }}>
            <div style={h2}>Momentum por mes</div>
            <div style={{ fontSize: 11.5, color: C.muted, marginBottom: 8 }}>Barras = colocado y cobrado · línea = interés</div>
            <div style={{ height: 220 }}>
              <ResponsiveContainer>
                <ComposedChart data={mensual} margin={{ top: 8, right: 4, left: -6, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f3f1ec" vertical={false} />
                  <XAxis dataKey="l" tick={{ fontSize: 11, fill: C.muted }} axisLine={false} tickLine={false} />
                  <YAxis yAxisId="l" tick={{ fontSize: 10, fill: C.muted }} axisLine={false} tickLine={false} width={44} tickFormatter={(v) => `${Math.round(v / 1000)}k`} />
                  <YAxis yAxisId="r" orientation="right" tick={{ fontSize: 10, fill: C.muted }} axisLine={false} tickLine={false} width={40} tickFormatter={(v) => `${(v / 1000).toFixed(1)}k`} />
                  <Tooltip content={<Tip fmt={(v: number) => formatQ(v)} />} cursor={{ fill: '#faf7f2' }} />
                  <Legend wrapperStyle={{ fontSize: 11 }} iconType="circle" iconSize={8} />
                  <Bar yAxisId="l" dataKey="colocado" name="Colocado" fill="#f5d3bd" radius={[6, 6, 0, 0]} />
                  <Bar yAxisId="l" dataKey="cobrado" name="Cobrado" fill="#9fd8b4" radius={[6, 6, 0, 0]} />
                  <Line yAxisId="r" type="monotone" dataKey="interes" name="Interés" stroke={C.brand} strokeWidth={3} dot={{ r: 4, fill: C.brand }} />
                </ComposedChart>
              </ResponsiveContainer>
            </div>
          </div>
          <div style={{ ...card, padding: 20 }}>
            <div style={h2}>Cómo se mueve el riesgo</div>
            <div style={{ fontSize: 11.5, color: C.muted, marginBottom: 8 }}>
              % del saldo por categoría, corte semanal · mora {moraSem >= 0 ? '+' : ''}{moraSem.toFixed(1)} pts vs semana pasada
            </div>
            <div style={{ height: 220 }}>
              <ResponsiveContainer>
                <AreaChart data={tend} margin={{ top: 8, right: 6, left: -22, bottom: 0 }} stackOffset="expand">
                  <XAxis dataKey="l" tick={{ fontSize: 10, fill: C.muted }} axisLine={false} tickLine={false} interval="preserveStartEnd" />
                  <YAxis tick={{ fontSize: 10, fill: C.muted }} axisLine={false} tickLine={false} tickFormatter={(v) => `${Math.round(v * 100)}%`} />
                  <Tooltip content={<Tip fmt={(v: number) => pct(v)} />} />
                  {CATS.map((c) => (
                    <Area key={c} type="monotone" dataKey={c} name={CAT[c].l} stackId="1" stroke={CAT[c].c} fill={CAT[c].c} fillOpacity={0.85} />
                  ))}
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>

        {/* 4 · RECUPERACIÓN: dónde se gana o se pierde la próxima semana */}
        <div style={{ ...card, padding: 20, marginTop: 14 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={{ display: 'grid', placeItems: 'center', width: 34, height: 34, borderRadius: 11, background: '#fff1e6', color: CAT.pre_mora.c }}><Lifebuoy size={19} weight="fill" /></div>
            <div>
              <div style={h2}>Lo que todavía se rescata</div>
              <div style={{ fontSize: 11.5, color: C.muted }}>Pre-mora se cobra antes del día 31 o pasa a mora</div>
            </div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: mobile ? '1fr 1fr' : 'repeat(5,1fr)', gap: 10, marginTop: 14 }}>
            <Mini label="Pre-mora · rescatable" value={formatQ(rec.rescatable_q ?? 0)} sub={`${rec.rescatable_n ?? 0} créditos · 15–30 d`} accent={CAT.pre_mora.c} />
            <Mini label="Atraso · 1–14 d" value={formatQ(rec.atraso_q ?? 0)} sub={`${at.n} créditos`} accent={CAT.atraso.c} />
            <Mini label="Recuperado · 30 d" value={formatQ(rec.recuperado_30?.q ?? 0)} sub={`${rec.recuperado_30?.n ?? 0} cuotas pagadas tarde`} accent={CAT.al_dia.c} />
            <Mini label="Vence esta semana" value={formatQ(rec.vence_semana_q ?? 0)} sub="cobrar antes de que venza" />
            <Mini label="Cobrado hoy" value={formatQ(rec.pagado_hoy_q ?? 0)} sub="pagos registrados" accent={CAT.al_dia.c} />
          </div>
        </div>

        {/* 5 · ESCALERA + COHORTES */}
        <div style={grid2}>
          <div style={{ ...card, padding: 20 }}>
            <div style={h2}>La escalera de recompra</div>
            <div style={{ fontSize: 11.5, color: C.muted, marginBottom: 14 }}>Peor atraso que tuvo cada crédito, según cuántas veces ha tomado el asociado</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {escalera.map((e) => (
                <div key={e.paso}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, marginBottom: 4 }}>
                    <b style={{ color: C.ink }}>{PASO[e.paso]}</b>
                    <span style={{ color: C.muted }}>{e.n} créditos · <b style={{ color: CAT.mora.c }}>{e.mora}% llegó a mora</b></span>
                  </div>
                  <div style={{ display: 'flex', height: 18, borderRadius: 8, overflow: 'hidden', background: '#f3f1ee' }}>
                    {CATS.map((c) => e[c] > 0 && (
                      <div key={c} title={`${CAT[c].l} ${e[c]}%`} style={{ width: `${e[c]}%`, background: CAT[c].c, color: '#fff', fontSize: 10, fontWeight: 700, display: 'grid', placeItems: 'center', overflow: 'hidden' }}>{e[c] >= 9 ? `${e[c]}%` : ''}</div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
            {escalera.length >= 2 && (
              <div style={{ fontSize: 12, color: C.ink2, marginTop: 14 }}>
                El que vuelve paga mejor: la mora baja de <b>{escalera[0].mora}%</b> en el primer crédito a <b>{escalera[1].mora}%</b> en el segundo.
              </div>
            )}
          </div>
          <div style={{ ...card, padding: 20 }}>
            <div style={h2}>Cohortes por mes de desembolso</div>
            <div style={{ fontSize: 11.5, color: C.muted, marginBottom: 12 }}>Dónde está hoy cada crédito otorgado ese mes</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              {cohortes.map((k) => {
                const seg = [
                  { l: 'Pagado', v: k.pagados, c: '#0f5132' }, { l: 'Al día', v: k.vivos_al_dia, c: CAT.al_dia.c },
                  { l: 'En gestión', v: k.en_gestion, c: CAT.pre_mora.c }, { l: 'Mora', v: k.en_mora, c: CAT.mora.c },
                ]
                return (
                  <div key={k.mes}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, marginBottom: 4 }}>
                      <b style={{ color: C.ink }}>{mesL(k.mes)}</b>
                      <span style={{ color: C.muted }}>{k.n} créditos · {formatQ(k.colocado)}</span>
                    </div>
                    <div style={{ display: 'flex', height: 18, borderRadius: 8, overflow: 'hidden', background: '#f3f1ee' }}>
                      {seg.map((s) => s.v > 0 && <div key={s.l} title={`${s.l} ${s.v}%`} style={{ width: `${s.v}%`, background: s.c, color: '#fff', fontSize: 10, fontWeight: 700, display: 'grid', placeItems: 'center', overflow: 'hidden' }}>{s.v >= 9 ? `${s.v}%` : ''}</div>)}
                    </div>
                  </div>
                )
              })}
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px 12px', fontSize: 11, color: C.muted, marginTop: 12 }}>
              {[['Pagado', '#0f5132'], ['Al día', CAT.al_dia.c], ['En gestión', CAT.pre_mora.c], ['Mora', CAT.mora.c]].map(([l, c]) => (
                <span key={l} style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}><span style={{ width: 8, height: 8, borderRadius: 2, background: c }} />{l}</span>
              ))}
            </div>
          </div>
        </div>

        {/* 6 · OPORTUNIDAD */}
        <div style={{ ...card, padding: 18, marginTop: 14, display: 'flex', alignItems: 'center', gap: 14, background: `linear-gradient(115deg, ${C.brandSoft}, #fff 62%)` }}>
          <div style={{ display: 'grid', placeItems: 'center', width: 42, height: 42, borderRadius: 14, background: '#ffe2cf', color: C.brand, flexShrink: 0 }}><Rocket size={21} weight="fill" /></div>
          <div style={{ fontSize: 13.5, color: C.ink }}>
            <div style={{ ...cap, color: C.brand }}>Próxima palanca</div>
            <b>{op.personas ?? 0} asociados</b> pagaron todo con menos de 15 días de atraso y hoy no tienen crédito. ~<b>{formatQ(op.monto_est ?? 0)}</b> listos para reactivar.
          </div>
        </div>

        <p style={{ fontSize: 11, color: C.muted, marginTop: 14 }}>
          Corte {d.hoy} · días calendario desde la cuota vencida más antigua sin pagar · actualizado {d.generado_at ? new Date(d.generado_at).toLocaleString('es-GT') : '—'}
        </p>
      </div>
    </div>
  )
}

function Big({ v, l, sub, c, mobile }: { v: string; l: string; sub: string; c: string; mobile: boolean }) {
  return (
    <div style={{ background: '#fff', border: `1px solid ${C.line}`, borderTop: `3px solid ${c}`, borderRadius: 14, padding: mobile ? '10px 10px' : '14px 16px' }}>
      <div style={{ fontFamily: DISPLAY, fontSize: mobile ? 22 : 32, fontWeight: 800, color: c, lineHeight: 1, letterSpacing: '-.02em', fontVariantNumeric: 'tabular-nums' }}>{v}</div>
      <div style={{ fontSize: mobile ? 11 : 12.5, fontWeight: 700, color: C.ink, marginTop: 6 }}>{l}</div>
      <div style={{ fontSize: mobile ? 10 : 11.5, color: C.muted }}>{sub}</div>
    </div>
  )
}
function Stat({ label, value, sub, accent }: { label: string; value: string; sub?: string; accent?: string }) {
  return (
    <div style={{ ...card, padding: 16 }}>
      <div style={cap}>{label}</div>
      <div style={{ fontFamily: DISPLAY, fontSize: 22, fontWeight: 800, color: accent ?? C.ink, letterSpacing: '-.02em', marginTop: 6, fontVariantNumeric: 'tabular-nums' }}>{value}</div>
      {sub && <div style={{ fontSize: 11, color: C.muted, marginTop: 3 }}>{sub}</div>}
    </div>
  )
}
function Mini({ label, value, sub, accent }: { label: string; value: string; sub?: string; accent?: string }) {
  return (
    <div style={{ borderRadius: 14, background: C.cream, padding: 14 }}>
      <div style={cap}>{label}</div>
      <div style={{ fontFamily: DISPLAY, fontSize: 18, fontWeight: 800, color: accent ?? C.ink, marginTop: 3, fontVariantNumeric: 'tabular-nums' }}>{value}</div>
      {sub && <div style={{ fontSize: 10.5, color: C.muted, marginTop: 2 }}>{sub}</div>}
    </div>
  )
}

export default NeoCoreBIMomentumPage
